import { ReactNode } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// zercode packages (real inbox data + shared date formatter)
import type { InboxItem } from '@zercode/api-client';
import { formatDateTime } from '@zercode/utils';

// project imports
import { withAlpha } from 'utils/colorUtils';

// assets — reuse Berry's own icon set for the per-item avatar treatment
import { IconBell, IconBuildingStore, IconMailbox } from '@tabler/icons-react';

function ListItemWrapper({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: withAlpha(theme.palette.grey[200], 0.3),
          ...theme.applyStyles('dark', { bgcolor: 'dark.900' })
        }
      }}
    >
      {children}
    </Box>
  );
}

// category → the icon Berry already uses for these avatar chips
const CATEGORY_ICON: Record<string, typeof IconBell> = {
  tenant_lifecycle: IconBuildingStore,
  billing: IconMailbox,
  system: IconBell
};

interface NotificationListProps {
  items: InboxItem[];
  loading?: boolean;
  onMarkRead: (id: string) => void;
  markingId?: string | null;
}

// ==============================|| NOTIFICATION LIST ITEM ||============================== //

export default function NotificationList({ items, loading, onMarkRead, markingId }: NotificationListProps) {
  const theme = useTheme();
  const containerSX = { gap: 2, pl: 7 };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Stack sx={{ gap: 2 }}>
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={48} />
        </Stack>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No notifications
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ width: '100%', maxWidth: { xs: 300, md: 330 }, py: 0 }}>
      {items.map((item) => {
        const Icon = CATEGORY_ICON[item.category] ?? IconBell;
        return (
          <ListItemWrapper key={item.id} onClick={item.isRead ? undefined : () => onMarkRead(item.id)}>
            <ListItem
              alignItems="center"
              disablePadding
              secondaryAction={
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Typography variant="caption">{formatDateTime(item.createdAtUtc)}</Typography>
                </Stack>
              }
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    color: 'primary.dark',
                    bgcolor: 'primary.light',
                    ...theme.applyStyles('dark', { bgcolor: 'dark.main' })
                  }}
                >
                  <Icon stroke={1.5} size="20px" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: item.isRead ? 400 : 600 }}>
                    {item.title}
                  </Typography>
                }
              />
            </ListItem>
            <Stack sx={{ ...containerSX, opacity: item.isRead ? 0.7 : 1 }}>
              <Typography variant="subtitle2">{item.body}</Typography>
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                <Chip label={item.category} size="small" variant="outlined" sx={{ width: 'min-content' }} />
                {!item.isRead && (
                  <Chip label={markingId === item.id ? 'Marking…' : 'Unread'} color="error" size="small" sx={{ width: 'min-content' }} />
                )}
              </Stack>
            </Stack>
          </ListItemWrapper>
        );
      })}
    </List>
  );
}
